import React, { useState } from 'react';
import { LayoutDashboard, Users, Settings, ChevronRight, ArrowLeft, ChevronDown, Check, LogOut, BookOpen, FileText, UserCog, ClipboardCheck } from 'lucide-react';
import { type Classroom } from '../../accounts';
import { DjayAvatar } from '../../mascot';
export type TeacherV2Route = 'today' | 'class' | 'subjects' | 'assessments' | 'reports' | 'management' | 'settings';
interface NavItem {
  id: TeacherV2Route;
  label: string;
  icon: React.ReactNode;
  group: 'Dagelijks' | 'Beoordeling' | 'Beheer';
}
const NAV_ITEMS: NavItem[] = [{
  id: 'today',
  label: 'Vandaag',
  icon: <LayoutDashboard className="w-5 h-5" data-id="element-6450" />,
  group: 'Dagelijks'
}, {
  id: 'class',
  label: 'Mijn Klas',
  icon: <Users className="w-5 h-5" data-id="element-6451" />,
  group: 'Dagelijks'
}, {
  id: 'subjects',
  label: 'Vakken',
  icon: <BookOpen className="w-5 h-5" data-id="element-6452" />,
  group: 'Dagelijks'
}, {
  id: 'assessments',
  label: 'Toetsen',
  icon: <ClipboardCheck className="w-5 h-5" data-id="element-6453" />,
  group: 'Beoordeling'
}, {
  id: 'reports',
  label: 'Rapporten',
  icon: <FileText className="w-5 h-5" data-id="element-6454" />,
  group: 'Beoordeling'
}, {
  id: 'management',
  label: 'Klas Beheer',
  icon: <UserCog className="w-5 h-5" data-id="element-6455" />,
  group: 'Beheer'
}];
interface TeacherSidebarV2Props {
  activeRoute: TeacherV2Route;
  onNavigate: (route: TeacherV2Route) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
  classroomName?: string;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  onSelectClassroom?: (id: string | null) => void;
  todayAttentionCount?: number;
}
export function TeacherSidebarV2({
  activeRoute,
  onNavigate,
  onLogout,
  onReturnHome,
  classroomName = 'Mijn Klas',
  classrooms = [],
  selectedClassroomId = null,
  onSelectClassroom,
  todayAttentionCount = 0
}: TeacherSidebarV2Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [showClassroomDropdown, setShowClassroomDropdown] = useState(false);
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const displayClassName = activeClassroom ? activeClassroom.name : classrooms.length > 0 ? 'Alle klassen' : classroomName;
  return <aside className={`flex flex-col bg-white border-r border-slate-200 transition-all duration-300 flex-shrink-0 h-full ${collapsed ? 'w-20' : 'w-72'}`} data-id="element-6456">
      {/* Header / Logo */}
      <div className={`flex items-center border-b border-slate-100 flex-shrink-0 h-20 ${collapsed ? 'justify-center px-2' : 'justify-between px-6'}`} data-id="element-6457">
        {!collapsed && <div className="flex items-center gap-3" data-id="element-6458">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden" data-id="element-6459">
              <DjayAvatar size="xs" emotion="happy" data-id="element-6460" />
            </div>
            <div className="flex flex-col" data-id="element-6461">
              <span className="text-sm font-black text-slate-900 leading-tight tracking-tight" data-id="element-6462">
                Meester djay
              </span>
              <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-6463">
                Docentportaal
              </span>
            </div>
          </div>}
        {collapsed && <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center border border-indigo-100 shadow-sm overflow-hidden" data-id="element-6464">
            <DjayAvatar size="xs" emotion="happy" data-id="element-6465" />
          </div>}
        {!collapsed && <button onClick={() => setCollapsed(true)} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none" aria-label="Inklappen" data-id="element-6466">
            <ChevronRight className="w-4 h-4 rotate-180" data-id="element-6467" />
          </button>}
      </div>

      {/* Classroom Selector */}
      {!collapsed && classrooms.length > 0 && onSelectClassroom && <div className="px-4 py-4 border-b border-slate-100 relative" data-id="element-6468">
          <button onClick={() => setShowClassroomDropdown(!showClassroomDropdown)} className="w-full flex items-center justify-between bg-white hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm" data-id="element-6469">
            <div className="flex flex-col items-start overflow-hidden" data-id="element-6470">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-6471">
                Huidige weergave
              </span>
              <span className="text-sm font-bold text-slate-900 truncate w-full text-left" data-id="element-6472">
                {displayClassName}
              </span>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showClassroomDropdown ? 'rotate-180' : ''}`} data-id="element-6473" />
          </button>

          {showClassroomDropdown && <>
              <div className="fixed inset-0 z-10" onClick={() => setShowClassroomDropdown(false)} data-id="element-6474" />
              <div className="absolute top-full left-4 right-4 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-20 overflow-hidden py-1.5" data-id="element-6475">
                <button onClick={() => {
            onSelectClassroom(null);
            setShowClassroomDropdown(false);
          }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === null ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-6476">
                  <span data-id="element-6477">Alle klassen</span>
                  {selectedClassroomId === null && <Check className="w-4 h-4" data-id="element-6478" />}
                </button>
                <div className="h-px bg-slate-100 my-1.5 mx-4" data-id="element-6479" />
                {classrooms.map(c => <button key={c.id} onClick={() => {
            onSelectClassroom(c.id);
            setShowClassroomDropdown(false);
          }} className={`w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors ${selectedClassroomId === c.id ? 'text-indigo-700 font-bold bg-indigo-50/50' : 'text-slate-700 font-medium'}`} data-id="element-6480">
                    <div className="flex items-center gap-2 truncate" data-id="element-6481">
                      <span className="truncate" data-id="element-6482">{c.name}</span>
                      <span className="text-[10px] py-0.5 px-2 bg-slate-100 text-slate-500 rounded-md font-bold" data-id="element-6483">
                        {c.learnerIds.length}
                      </span>
                    </div>
                    {selectedClassroomId === c.id && <Check className="w-4 h-4 flex-shrink-0" data-id="element-6484" />}
                  </button>)}
              </div>
            </>}
        </div>}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-6 px-3" data-id="element-6485">
        <div className="space-y-1.5" data-id="element-6486">
          {(() => {
          const groups = ['Dagelijks', 'Beoordeling', 'Beheer'] as const;
          return groups.map((group, index) => {
            const groupItems = NAV_ITEMS.filter(item => item.group === group);
            if (groupItems.length === 0) return null;
            return <div key={group} className={index > 0 ? 'pt-2' : ''} data-id="element-6487">
                  {index > 0 && <div className="h-px bg-slate-100 my-3 mx-2" data-id="element-6488" />}
                  {!collapsed && <div className="px-4 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-6489">
                      {group}
                    </div>}
                  <div className="space-y-1.5" data-id="element-6490">
                    {groupItems.map(item => {
                  const isActive = activeRoute === item.id;
                  const badgeCount = item.id === 'today' ? todayAttentionCount : 0;
                  return <button key={item.id} onClick={() => onNavigate(item.id)} title={collapsed ? item.label : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center px-2 py-3' : 'px-4 py-3'} ${isActive ? 'bg-indigo-50 text-indigo-700 font-bold shadow-sm border border-indigo-100/50' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 font-medium border border-transparent'}`} data-id="element-6491">
                          <span className={`relative flex-shrink-0 transition-colors ${isActive ? 'text-indigo-600' : 'text-slate-400 group-hover:text-slate-600'}`} data-id="element-6492">
                            {item.icon}
                            {badgeCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none" data-id="element-6493">
                                {badgeCount > 9 ? '9+' : badgeCount}
                              </span>}
                          </span>
                          {!collapsed && <span className="text-sm truncate" data-id="element-6494">
                              {item.label}
                            </span>}
                        </button>;
                })}
                  </div>
                </div>;
          });
        })()}
        </div>
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 border-t border-slate-100 ${collapsed ? 'p-3' : 'p-5'} space-y-2`} data-id="element-6495">
        {collapsed && <button onClick={() => setCollapsed(false)} className="w-full flex items-center justify-center p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors mb-2" aria-label="Uitklappen" data-id="element-6496">
            <ChevronRight className="w-5 h-5" data-id="element-6497" />
          </button>}

        <button onClick={() => onNavigate('settings')} title={collapsed ? 'Instellingen' : undefined} className={`w-full flex items-center gap-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5'} ${activeRoute === 'settings' ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 font-medium'}`} data-id="element-6498">
          <Settings className={`w-5 h-5 ${activeRoute === 'settings' ? 'text-indigo-600' : ''}`} data-id="element-6499" />
          {!collapsed && <span className="text-sm" data-id="element-6500">Instellingen</span>}
        </button>

        {onLogout && <button onClick={onLogout} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Uitloggen' : undefined} data-id="element-6501">
            <LogOut className="w-5 h-5" data-id="element-6502" />
            {!collapsed && <span className="text-sm" data-id="element-6503">Uitloggen</span>}
          </button>}

        {onReturnHome && <button onClick={onReturnHome} className={`w-full flex items-center gap-3 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-colors focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:outline-none ${collapsed ? 'justify-center p-2.5' : 'px-4 py-2.5 font-medium'}`} title={collapsed ? 'Naar leerlingomgeving' : undefined} data-id="element-6504">
            <ArrowLeft className="w-5 h-5" data-id="element-6505" />
            {!collapsed && <span className="text-sm" data-id="element-6506">Leerlingomgeving</span>}
          </button>}
      </div>
    </aside>;
}